import React, { useState, useEffect, useCallback } from "react";
import { customFetch } from '../apiClient';
import * as XLSX from "xlsx"; 
import defaultLogo from "../assests/kamlaLogo.png"; 

const FinancialReports = () => {
  const [activeTab, setActiveTab] = useState("profit-loss");
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(0); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0]
  });

  const [globalSearch, setGlobalSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleFilterChange = (e) => setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    const fetchCompanyInfo = async () => {
   try {
        const res = await customFetch(`/PharmaCompany`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0) setCompanyInfo(data[0]);
        }
      } catch (err) { console.error("Failed to fetch company info", err); }
    };
    fetchCompanyInfo();
  }, []);

  const fetchReport = async () => {
    setLoading(true); setError(""); setReportData([]); setSummary(0); setCurrentPage(1);
    try {
      const params = new URLSearchParams({ fromDate: filters.fromDate, toDate: filters.toDate });

      let endpoint = activeTab === "profit-loss" ? "profit-loss" : "gst-report";
      const res = await customFetch(`/FinancialReports/${endpoint}?${params.toString()}`);

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      if (activeTab === "profit-loss") {
          setReportData(data.items || []);
          setSummary(data.grandTotalProfit || 0);
      } else {
          setReportData(data || []); 
      }

    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  useEffect(() => { setGlobalSearch(""); fetchReport(); }, [activeTab]);

  const filteredData = reportData.filter(item => {
      if (!globalSearch || activeTab === 'gst-report') return true;
      const searchLower = globalSearch.toLowerCase();
      const medName = (item.medicine || item.Medicine || '').toLowerCase();
      return medName.includes(searchLower);
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const displayedData = activeTab === 'gst-report' ? filteredData : filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  // ==============================================================
  // ✅ EXCEL EXPORT
  // ==============================================================
  const exportToExcel = useCallback(() => {
    if (filteredData.length === 0) { alert("No data available to export!"); return; }

    let formattedData = [];
    if (activeTab === 'profit-loss') {
      formattedData = filteredData.map((item, i) => ({
        "Sr": i + 1, "Medicine": item.medicine || item.Medicine, 
        "Sold Qty": `${item.totalQtySold || item.TotalQtySold} ${item.unit || item.Unit}`, 
        "Avg Sale Rate (₹)": item.avgSaleRate || item.AvgSaleRate, "Pur. Rate (₹)": item.purchaseRate || item.PurchaseRate,
        "Revenue (₹)": item.totalRevenue || item.TotalRevenue, "Cost/COGS (₹)": item.totalCost || item.TotalCost,
        "Gross Profit (₹)": item.grossProfit || item.GrossProfit, "Margin (%)": item.marginPercentage || item.MarginPercentage
      }));
    } else if (activeTab === 'gst-report') {
      formattedData = filteredData.map((item, i) => ({
        "Sr": i + 1, "Type": item.type || item.Type, "GST Slab (%)": item.slab || item.Slab,
        "Taxable Value (₹)": item.taxableAmount || item.TaxableAmount, "GST Amount (₹)": item.taxAmount || item.TaxAmount, 
        "Total Value (₹)": item.totalAmount || item.TotalAmount
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `FINANCIAL_${activeTab.toUpperCase()}_${filters.fromDate}_to_${filters.toDate}.xlsx`);
  }, [filteredData, activeTab, filters]);

  // ==============================================================
  // ✅ TALLY STYLE PRINT
  // ==============================================================
  const handlePrint = useCallback(() => {
    if (filteredData.length === 0) { alert("No data available to print!"); return; }

    const logoSource = companyInfo?.logoBase64 ? `data:image/png;base64,${companyInfo.logoBase64}` : defaultLogo;
    const compName = companyInfo?.company_Name || "HOSPITAL / PHARMACY STORE";
    const compAddress = companyInfo?.address || "";
    const compContact = companyInfo?.contact1 ? `Ph: ${companyInfo.contact1}` : "";
    const compGst = companyInfo?.reg_No ? `DL/GST: ${companyInfo.reg_No}` : "";

    const ROWS_PER_PAGE = 45; 
    const pages = [];
    let runningTotal = 0;

    for (let i = 0; i < filteredData.length; i += ROWS_PER_PAGE) {
      const pageItems = filteredData.slice(i, i + ROWS_PER_PAGE);
      const broughtForward = runningTotal;
      
      const pageTotal = pageItems.reduce((sum, item) => {
          if (activeTab === 'profit-loss') return sum + (item.grossProfit || item.GrossProfit || 0);
          return sum + (item.taxAmount || item.TaxAmount || 0); // For GST report
      }, 0);

      runningTotal += pageTotal;
      
      pages.push({
        items: pageItems,
        startIndex: i,
        broughtForward: broughtForward,
        carriedForward: runningTotal,
        isLastPage: i + ROWS_PER_PAGE >= filteredData.length
      });
    }

    const reportHtml = pages.map((page, pageIndex) => {
      let thHtml = "";
      if (activeTab === 'profit-loss') thHtml = `<th style="text-align:left">Medicine</th><th style="text-align:center">Sold Qty</th><th style="text-align:right">Avg Sale Rate</th><th style="text-align:right">Pur. Rate</th><th style="text-align:right">Revenue</th><th style="text-align:right">Cost</th><th style="text-align:right">Profit</th>`;
      else if (activeTab === 'gst-report') thHtml = `<th style="text-align:left">Transaction Type</th><th style="text-align:center">GST Slab</th><th style="text-align:right">Taxable Value</th><th style="text-align:right">GST Amount</th><th style="text-align:right">Total Value</th>`;

      const trHtml = page.items.map((item, idx) => {
        if (activeTab === 'profit-loss') return `<tr><td style="text-align:left"><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:center">${item.totalQtySold || item.TotalQtySold} ${item.unit || item.Unit}</td><td style="text-align:right">${(item.avgSaleRate || item.AvgSaleRate || 0).toFixed(2)}</td><td style="text-align:right">${(item.purchaseRate || item.PurchaseRate || 0).toFixed(2)}</td><td style="text-align:right">${(item.totalRevenue || item.TotalRevenue || 0).toFixed(2)}</td><td style="text-align:right">${(item.totalCost || item.TotalCost || 0).toFixed(2)}</td><td style="text-align:right; font-weight:bold; color:${(item.grossProfit || item.GrossProfit) >= 0 ? '#16a34a' : '#dc2626'};">${(item.grossProfit || item.GrossProfit || 0).toFixed(2)}</td></tr>`;
        if (activeTab === 'gst-report') return `<tr><td style="text-align:left; font-weight:bold;">${item.type || item.Type}</td><td style="text-align:center">${item.slab || item.Slab}%</td><td style="text-align:right">${(item.taxableAmount || item.TaxableAmount || 0).toFixed(2)}</td><td style="text-align:right; font-weight:bold;">${(item.taxAmount || item.TaxAmount || 0).toFixed(2)}</td><td style="text-align:right">${(item.totalAmount || item.TotalAmount || 0).toFixed(2)}</td></tr>`;
      }).join('');

      let colSpanBF = activeTab === 'profit-loss' ? '6' : '3';
      const totalLabel = activeTab === 'profit-loss' ? "Total Gross Profit (₹):" : "Total GST Tax (₹):";

      const bfHtml = pageIndex > 0 ? `
        <tr style="background:#f9f9f9; border-bottom: 1px solid #000;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-style:italic;">Brought Forward (B/F):</td>
          <td style="text-align:right; font-weight:bold;">${page.broughtForward.toFixed(2)}</td>
          ${activeTab === 'gst-report' ? '<td></td>' : ''}
        </tr>` : "";

      const cfHtml = `
        <tr style="background:#e0e0e0;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">
             ${page.isLastPage ? `GRAND TOTAL (${totalLabel}):` : 'Carried Forward (C/F):'}
          </td>
          <td style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">${page.carriedForward.toFixed(2)}</td>
          ${activeTab === 'gst-report' ? '<td></td>' : ''}
        </tr>`;

      return `
        <div class="a4-page ${page.isLastPage ? 'last-page' : ''}">
          <div class="marg-header">
            <img src="${logoSource}" alt="Logo" class="print-logo"/>
            <div class="marg-header-center">
              <h2>${compName}</h2>
              <p>${compAddress}</p>
              <p><strong>${compContact}</strong></p>
            </div>
            <div class="marg-header-right">
              ${compGst ? `<span><strong>${compGst}</strong></span><br/>` : ""}
              <span><strong>Page:</strong> ${pageIndex + 1} of ${pages.length}</span><br/>
              <span><strong>Print Date:</strong> ${new Date().toLocaleDateString('en-GB')}</span>
            </div>
          </div>
          
          <div class="marg-title-strip">
            ${activeTab.toUpperCase().replace('-', ' ')} REPORT
            <br/>(FROM: ${formatDate(filters.fromDate)} TO: ${formatDate(filters.toDate)})
          </div>

          <div class="content-area">
            <table class="items-table">
              <thead><tr>${thHtml}</tr></thead>
              <tbody>${bfHtml}${trHtml}</tbody>
            </table>
          </div>
          
          <div class="footer-area">
             <table class="items-table" style="margin:0; border-top: 2px solid #000;"><tbody>${cfHtml}</tbody></table>
          </div>
        </div>
      `;
    }).join("");

    const pw = window.open("", "_blank", "width=900,height=700");
    pw.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${activeTab.toUpperCase()} Report</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
          body { background: #525659; display: flex; flex-direction: column; align-items: center; padding: 20px; }
          @media print {
            @page { size: A4 portrait; margin: 8mm; }
            body { background: #fff; padding: 0; margin: 0;}
            .a4-page { width: 100% !important; min-height: 275mm; page-break-after: always; margin: 0 !important; border: 1px solid #000 !important; }
            .a4-page.last-page { page-break-after: auto; }
          }
          .a4-page { background: #fff; width: 210mm; min-height: 280mm; border: 2px solid #000; margin-bottom: 20mm; display: flex; flex-direction: column; box-shadow: 0 0 10px rgba(0,0,0,0.5); }
          .marg-header { border-bottom: 2px solid #000; padding: 10px 15px; display: flex; justify-content: space-between; align-items: center; }
          .print-logo { width: 75px; height: 75px; object-fit: contain; flex-shrink:0;}
          .marg-header-center { flex-grow: 1; padding: 0 15px; text-align:center;}
          .marg-header-center h2 { margin: 0 0 4px 0; font-size: 18px; text-transform: uppercase; }
          .marg-title-strip { background-color: #e0e0e0 !important; border-bottom: 2px solid #000; text-align: center; padding: 5px; font-weight: bold; font-size: 13px; -webkit-print-color-adjust: exact !important;}
          .content-area { flex-grow: 1; padding: 0; } 
          .footer-area { margin-top: auto; }
          .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .items-table th { border-bottom: 2px solid #000; border-right: 1px solid #ccc; background-color: #f1f1f1 !important; padding: 6px 8px; font-weight: bold; text-transform: uppercase; -webkit-print-color-adjust: exact !important;}
          .items-table td { border-right: 1px dashed #ccc; border-bottom: 1px dashed #ccc; padding: 5px 8px; }
          .items-table th:last-child, .items-table td:last-child { border-right: none; }
        </style>
      </head>
      <body>${reportHtml}</body>
      </html>
    `);
    pw.document.close();
    setTimeout(() => { pw.focus(); pw.print(); }, 500);
  }, [filteredData, activeTab, filters, companyInfo]);

  useEffect(() => {
    const handleExportEvent = (e) => {
      const exportType = e.detail?.type;
      if (exportType === 'excel') exportToExcel();
      else if (exportType === 'pdf' || exportType === 'print') handlePrint(); 
    };
    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [exportToExcel, handlePrint]);

  return (
    <div className="report-container">
      <div className="report-card">
        
        <div className="card-header">
          <div className="header-left">
             <div className="header-icon"><i className="bi bi-graph-up-arrow"></i></div>
             <div className="header-text"><h5>Financial & GST Reports</h5><p>Profit Analysis & Tax Summaries</p></div>
          </div>
          <button className="btn-header-action" onClick={fetchReport} disabled={loading}>
             <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i> Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="tabs-header">
           <button className={`tab-btn ${activeTab === 'profit-loss' ? 'active' : ''}`} onClick={() => setActiveTab('profit-loss')}>
              <i className="bi bi-cash-coin"></i> Profit & Loss (Item Wise)
           </button>
           <button className={`tab-btn ${activeTab === 'gst-report' ? 'active' : ''}`} onClick={() => setActiveTab('gst-report')}>
              <i className="bi bi-bank"></i> GST Summary
           </button>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger">{error}</div>}

          {/* Filters Bar */}
          <div className="filter-bar">
             <div className="form-group">
                <label>From Date</label>
                <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} className="form-input" />
             </div>
             <div className="form-group">
                <label>To Date</label>
                <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} className="form-input" />
             </div>

             {activeTab === 'profit-loss' && (
                 <div className="form-group flex-grow-1" style={{maxWidth: '300px'}}>
                    <label>Quick Search:</label>
                    <input type="text" className="form-input" placeholder="Search by Medicine..." 
                           value={globalSearch} onChange={(e) => { setGlobalSearch(e.target.value); setCurrentPage(1); }} />
                 </div>
             )}

             <button className="btn-search" onClick={fetchReport} disabled={loading}>
                {loading ? 'Wait...' : 'Generate Report'}
             </button>
          </div>

          {/* Profit Summary Card */}
          {activeTab === 'profit-loss' && !loading && displayedData.length > 0 && (
             <div className="summary-cards">
                <div className={`s-card ${summary >= 0 ? 'total' : 'returns'}`}>
                   <div className="s-label">Total Gross Profit for Period</div>
                   <div className="s-value">₹ {summary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
             </div>
          )}

          {/* Tables */}
          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  {activeTab === 'profit-loss' && (
                     <tr>
                        <th className="text-center" style={{width:'40px'}}>#</th>
                        <th className="text-start">Medicine Name</th>
                        <th className="text-center">Sold Qty</th>
                        <th className="text-end">Avg Sale Rate</th>
                        <th className="text-end">Pur. Rate</th>
                        <th className="text-end">Revenue</th>
                        <th className="text-end">Cost (COGS)</th>
                        <th className="text-end">Profit</th>
                        <th className="text-center">Margin %</th>
                     </tr>
                  )}
                  {activeTab === 'gst-report' && (
                     <tr>
                        <th className="text-start">Transaction Type</th>
                        <th className="text-center">GST Slab (%)</th>
                        <th className="text-end">Taxable Value</th>
                        <th className="text-end">GST Amount</th>
                        <th className="text-end">Total Value</th>
                     </tr>
                  )}
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="empty-state">Analysing financial data...</td></tr>
                  ) : displayedData.length === 0 ? (
                    <tr><td colSpan="10" className="empty-state">No financial records found.</td></tr>
                  ) : (
                    displayedData.map((item, index) => (
                      <tr key={index}>
                        
                        {activeTab === 'profit-loss' && <>
                           <td className="text-center text-muted text-sm">{(currentPage - 1) * rowsPerPage + index + 1}</td>
                           <td className="text-start fw-bold">{item.medicine || item.Medicine}</td>
                           <td className="text-center fw-medium">{item.totalQtySold || item.TotalQtySold} <small className="text-muted">{item.unit || item.Unit}</small></td>
                           <td className="text-end">₹{(item.avgSaleRate || item.AvgSaleRate || 0).toFixed(2)}</td>
                           <td className="text-end text-muted">₹{(item.purchaseRate || item.PurchaseRate || 0).toFixed(2)}</td>
                           <td className="text-end fw-medium text-primary">₹{(item.totalRevenue || item.TotalRevenue || 0).toFixed(2)}</td>
                           <td className="text-end">₹{(item.totalCost || item.TotalCost || 0).toFixed(2)}</td>
                           <td className={`text-end fw-bold ${(item.grossProfit || item.GrossProfit) >= 0 ? 'text-success' : 'text-danger'}`}>
                              ₹{(item.grossProfit || item.GrossProfit || 0).toFixed(2)}
                           </td>
                           <td className="text-center">
                              <span className={`badge-margin ${(item.marginPercentage || item.MarginPercentage) > 20 ? 'bg-in' : 'bg-out'}`}>
                                 {(item.marginPercentage || item.MarginPercentage || 0).toFixed(2)}%
                              </span>
                           </td>
                        </>}

                        {activeTab === 'gst-report' && <>
                           <td className="text-start fw-bold">{item.type || item.Type}</td>
                           <td className="text-center"><span className="badge-gst">{item.slab || item.Slab}%</span></td>
                           <td className="text-end fw-medium">₹{(item.taxableAmount || item.TaxableAmount || 0)?.toFixed(2)}</td>
                           <td className="text-end fw-bold text-primary">₹{(item.taxAmount || item.TaxAmount || 0)?.toFixed(2)}</td>
                           <td className="text-end">₹{(item.totalAmount || item.TotalAmount || 0)?.toFixed(2)}</td>
                        </>}

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls - Only for Profit Loss */}
            {!loading && activeTab === 'profit-loss' && filteredData.length > 0 && (
              <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'11px'}}>Rows per page:</span>
                  <select 
                    className="form-select form-select-sm" 
                    style={{width: '70px', fontSize: '11px', padding: '2px 10px'}}
                    value={rowsPerPage} 
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'11px', marginRight:'10px'}}>
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
                  </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  ><i className="bi bi-chevron-left"></i> Prev</button>
                  <span className="fw-bold" style={{fontSize:'11px'}}> Page {currentPage} of {totalPages} </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === totalPages} 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >Next <i className="bi bi-chevron-right"></i></button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ✅ CSS STYLES */}
      <style jsx>{`
        .report-container { padding: 0; font-size: 12px; }
        .report-card { background: white; border-radius: 8px; box-shadow: 0 1px 10px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; overflow: hidden; }
        
        .card-header { background: var(--theme-color, #0088cc); padding: 8px 15px; display: flex; justify-content: space-between; align-items: center; }
        .header-left { display: flex; align-items: center; gap: 10px; color: white; }
        .header-icon { width: 32px; height: 32px; background: rgba(255, 255, 255, 0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .header-text h5 { margin: 0; font-size: 15px; font-weight: 700; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 11px; }

        .btn-header-action { padding: 5px 12px; border-radius: 4px; border: 1px solid rgba(255, 255, 255, 0.4); background: rgba(255, 255, 255, 0.15); color: white; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 6px; }
        .btn-header-action:hover { background: rgba(255, 255, 255, 0.25); }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }

        .tabs-header { display: flex; background: #f8fafc; border-bottom: 1px solid #e0e6ed; padding: 0 15px; gap: 5px; overflow-x: auto; }
        .tab-btn { padding: 10px 14px; border: none; background: transparent; color: #64748b; font-weight: 600; font-size: 12px; cursor: pointer; border-bottom: 2px solid transparent; display: flex; gap: 6px; align-items: center; white-space: nowrap; }
        .tab-btn:hover { color: var(--theme-color, #0088cc); }
        .tab-btn.active { color: var(--theme-color, #0088cc); border-bottom-color: var(--theme-color, #0088cc); background: white; border-radius: 6px 6px 0 0; border: 1px solid #e0e6ed; border-bottom: none; position: relative; top: 1px; }

        .card-content { padding: 15px; }

        .filter-bar { display: flex; gap: 8px; background: #f8fafc; padding: 10px; border-radius: 6px; border: 1px solid #e0e6ed; margin-bottom: 12px; align-items: flex-end; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: 11px; font-weight: 600; margin-bottom: 3px; color: #475569; }
        .form-input { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 12px; height: 34px; outline: none; }
        .btn-search { padding: 0 15px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-weight: 600; cursor: pointer; height: 34px; font-size: 12px; }
        .btn-search:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Summary Cards */
        .summary-cards { display: flex; justify-content: flex-end; margin-bottom: 15px; }
        .s-card { padding: 12px 25px; border-radius: 8px; color: white; text-align: right; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .s-card.total { background: linear-gradient(135deg, #10b981, #059669); }
        .s-card.returns { background: linear-gradient(135deg, #ef4444, #b91c1c); }
        .s-label { font-size: 11px; opacity: 0.9; margin-bottom: 3px; text-transform: uppercase; }
        .s-value { font-size: 20px; font-weight: 700; }

        .table-section { background: white; border-radius: 6px; border: 1px solid #e0e6ed; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .custom-table th { background: #f8fafc; color: #334155; padding: 8px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #1e293b; }
        .custom-table tr:hover { background-color: #f8fafc; }

        .text-start { text-align: left !important; }
        .text-center { text-align: center !important; }
        .text-end { text-align: right !important; }
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 500; }
        .text-success { color: #16a34a !important; }
        .text-danger { color: #dc2626 !important; }
        .text-primary { color: var(--theme-color, #0088cc) !important; }
        .text-muted { color: #64748b !important; }
        .text-sm { font-size: 10px; }

        .badge-margin { padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }
        .bg-in { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0;}
        .bg-out { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca;}
        
        .badge-gst { background: #e0f2fe; color: #0369a1; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px; border: 1px solid #bae6fd;}

        .empty-state { text-align: center; padding: 40px; color: #94a3b8; font-style: italic; }
        .alert-custom { padding: 10px 14px; border-radius: 8px; font-size: 12px; margin-bottom: 14px; }
        .alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
      `}</style>
    </div>
  );
};

export default FinancialReports;